/**
 * Lazy-loaded Vue overlays: the value-trend popover and modal on ROS rankings, the player
 * modal, and the intent-based preloads that start their downloads before a click lands.
 *
 * Calls on sibling mixins via `this`: UrlAndScoring (getPprSuperflexSlug), UserLeague
 * (hasUserLeagueSelected, selectedUserLeagueId, cachedLeagueData, _userLeagueDataPromise),
 * CoreState (basePath, isLargeScreen).
 */
import { loadPlayerModalApp, loadTrendModalApp } from './../modules/PlayerModalLoader.js';
import { getArrItemByPropValue } from './../../helpers/DsHelper.js';

export default () => ({
    // Hover token and loaded trend module, set by the open/close methods below
    _trendHoverId: 0,
    _trendModule: null,

    initModalTriggers() {
        document.body.addEventListener('open-player-modal', e => {
            const playerName = e.target.closest && e.target.closest('player-name[modal]');
            const tbody = playerName && playerName.closest('tbody[data-player-row]');
            if (!tbody) {
                return;
            }
            this.openPlayerModal(tbody.dataset.key, playerName);
        });
        // Start loading the player modal or the trend chart as soon as a visitor heads for
        // one, so it is usually ready by the time the click lands
        ['pointerover', 'touchstart', 'focusin'].forEach(eventName => {
            document.body.addEventListener(
                eventName,
                e => {
                    if (!e.target.closest) {
                        return;
                    }
                    if (e.target.closest('.rankings-trend-cell')) {
                        this.preloadTrendModal();
                        return;
                    }
                    if (e.target.closest('.open-player-modal')) {
                        this.preloadPlayerModal();
                    }
                },
                { passive: true }
            );
        });

        if (this.basePath === 'ros-rankings') {
            // mouseover travels up to this body listener, so one listener covers cells swapped in later.
            // Desktop only: a small screen taps straight through to the modal.
            // relatedTarget ignores moves that stay inside the same cell.
            // A league selection hides the column, so drop a popover still open on it.
            this.$watch('selectedUserLeagueId', () => this.closeTrendPopover());
            document.body.addEventListener('mouseover', e => {
                const cell = e.target.closest && e.target.closest('.rankings-trend-cell');
                if (!cell || !this.isLargeScreen || cell.contains(e.relatedTarget)) {
                    return;
                }
                this.openTrendFromCell(cell);
            });
            document.body.addEventListener('mouseout', e => {
                const cell = e.target.closest && e.target.closest('.rankings-trend-cell');
                if (!cell || cell.contains(e.relatedTarget)) {
                    return;
                }
                this.closeTrendPopover();
            });
        }
    },

    /**
     * Default redraft board matching the current PPR/superflex slug. Trend history
     * is stored on that board.
     * @returns {number|null}
     */
    getTrendBoardId() {
        const slug = this.getPprSuperflexSlug();
        const match = (window.appData?.scoringConfigurations || []).find(
            config =>
                config.pprSuperflexSlug === slug && config.leagueType === 'redraft' && !String(config.id).startsWith('mvpBoardScoringId')
        );
        return match?.mvpBoardScoringId ?? null;
    },

    /**
     * Player the trend chart expects, read off the cell. ROS stores the team on
     * team_abbr. The board id is the default board from getTrendBoardId().
     * @param {HTMLElement} cell
     * @returns {object|null}
     */
    trendPlayerFromCell(cell) {
        const row = cell.closest('tbody[data-player-row]');
        if (!row) {
            return null;
        }
        const marketValue = cell.dataset.marketValue;
        return {
            id: Number(row.dataset.key),
            first_name: cell.dataset.firstName || '',
            last_name: cell.dataset.lastName || '',
            team_abbr: cell.dataset.team || '',
            fantasy_position: cell.dataset.position || row.dataset.fantasyPosition || '',
            rankFantasyPositionDsValue: cell.dataset.posRank || '',
            birthday: cell.dataset.birthday || null,
            dsValue: cell.dataset.dsValue === '' || cell.dataset.dsValue == null ? null : Number(cell.dataset.dsValue),
            marketValue: marketValue === '' || marketValue == null ? null : Number(marketValue),
            mvpBoardScoringId: this.getTrendBoardId(),
        };
    },

    /**
     * Opens the hover popover. _trendHoverId drops the result when the pointer
     * has already left, or a click has taken over, before the module finishes loading.
     * @param {HTMLElement} cell
     * @returns {Promise<void>}
     */
    async openTrendFromCell(cell) {
        if (!window.appData?.trendModal) {
            return;
        }
        this._trendHoverId = (this._trendHoverId || 0) + 1;
        const hoverId = this._trendHoverId;
        const player = this.trendPlayerFromCell(cell);
        if (!player?.id || !player.mvpBoardScoringId) {
            return;
        }
        try {
            const mod = await loadTrendModalApp(window.appData.trendModal);
            if (hoverId !== this._trendHoverId) {
                return;
            }
            this._trendModule = mod;
            await mod.openTrendPopover(player, cell);
        } catch (err) {
            console.error(err);
        }
    },

    /**
     * Opens the trend modal from a click or tap, and cancels a popover that is
     * still loading for this cell so it does not open over the modal.
     * @param {HTMLElement} cell
     * @returns {Promise<void>}
     */
    async openTrendModalFromCell(cell) {
        if (!window.appData?.trendModal) {
            return;
        }
        this._trendHoverId = (this._trendHoverId || 0) + 1;
        const player = this.trendPlayerFromCell(cell);
        if (!player?.id || !player.mvpBoardScoringId) {
            return;
        }
        cell.setAttribute('aria-busy', 'true');
        try {
            const mod = await loadTrendModalApp(window.appData.trendModal);
            this._trendModule = mod;
            await mod.openTrendModal(player);
        } catch (err) {
            console.error(err);
            window.alert('The trend chart could not be loaded. Please try again.');
        } finally {
            cell.removeAttribute('aria-busy');
        }
    },

    /**
     * Closes the popover and cancels one that has not finished opening.
     * @returns {void}
     */
    closeTrendPopover() {
        this._trendHoverId = (this._trendHoverId || 0) + 1;
        this._trendModule?.closeTrendPopover();
    },

    /**
     * Starts the player-modal download before the click lands.
     * @returns {void}
     */
    preloadPlayerModal() {
        if (!window.appData?.playerModal) {
            return;
        }
        loadPlayerModalApp(window.appData.playerModal).catch(err => console.error(err));
    },

    /**
     * Starts the trend-chart download before the click lands. Separate from the
     * player modal, so hovering a name does not fetch the chart.
     * @returns {void}
     */
    preloadTrendModal() {
        if (!window.appData?.trendModal) {
            return;
        }
        loadTrendModalApp(window.appData.trendModal).catch(err => console.error(err));
    },
    /**
     * Gets the selected user league's scoring for the player modal's "Custom" entry
     * @returns {object|null}
     */
    getPlayerModalCustomScoring() {
        if (!this.hasUserLeagueSelected()) {
            return null;
        }
        const scoringConfig = window.appData?.scoringConfigurations?.find(
            scoringConfig => scoringConfig.id === `mvpBoardScoringId${this.selectedUserLeagueId}`
        );
        if (!scoringConfig) {
            return null;
        }
        return {
            pprScoring: scoringConfig.pprScoring || '',
            superflex: scoringConfig.superflex || false,
            leagueType: scoringConfig.leagueType,
        };
    },

    /**
     * Gets a player as the board processor scored them for the selected user league, once
     * the calculation the league selection started has finished
     * @param {string|number} playerId
     * @returns {Promise<object|null>}
     */
    async getUserLeaguePlayerForModal(playerId) {
        await this._userLeagueDataPromise;
        const leaguePlayers = this.cachedLeagueData[this.selectedUserLeagueId]?.players ?? [];
        return getArrItemByPropValue(playerId, leaguePlayers, 'player_id', false) ?? null;
    },

    /**
     * Opens the Vue player modal for a player, loading it first when needed. The modal uses
     * the default scoring that matches the page's PPR/superflex selection, or the selected
     * user league's scoring when there is one.
     * @param {string|number} playerId
     * @param {HTMLElement} button The button that was clicked, flagged busy while loading
     * @returns {Promise<void>}
     */
    async openPlayerModal(playerId, button) {
        if (!window.appData?.playerModal) {
            return;
        }
        // Best ball defaults to half PPR when the page has no scoring selection of its own
        const pprSuperflexSlug = this.getPprSuperflexSlug() || (this.basePath === 'best-ball-rankings' ? 'half-ppr' : '');
        const customScoring = this.getPlayerModalCustomScoring();
        // The league numbers may still be computing; the modal opens and waits for them
        const leaguePlayer = customScoring ? this.getUserLeaguePlayerForModal(playerId) : null;
        button.setAttribute('aria-busy', 'true');
        try {
            const playerModalApp = await loadPlayerModalApp(window.appData.playerModal);
            await playerModalApp.openPlayerModal(playerId, { pprSuperflexSlug, customScoring, leaguePlayer });
        } catch (err) {
            console.error(err);
            window.alert('The player details could not be loaded. Please try again.');
        } finally {
            button.removeAttribute('aria-busy');
        }
    },
});
